import { IDayLoad } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment-timezone';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { IAdminUserSessionComponentProps } from '../../../../models/AdminModels';
import { UserSessionViewModelKey } from '../../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../models/Logging';
import { AdminEmailWorkloadViewModel } from '../../../../viewmodels/AdminViewModels';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { ConfigureScheduledSend } from '../../../../web/components/email/ConfigureScheduledSend';
import { styleSheet } from './styles';

interface IProps
	extends RouteComponentProps<any>,
		IAdminUserSessionComponentProps,
		IImpersonationContextComponentProps,
		IEventLoggingComponentProps {
	className?: string;
	onRenderMessage?(datePassed: boolean): string;
	onSelected?(): void;
	onSendDateUpdated?(sendDate: Date): void;
	sendDate?: Date;
	styles?: StyleDeclarationValue[];
	submitButtonText?: string;
}

interface IState {
	accountEmailWorkloadForSelectedMonth?: IDayLoad[];
	month?: Date;
}

class _BulkEmailApprovalConfigureSchedule extends React.Component<IProps, IState> {
	private mWorkloadViewModel: AdminEmailWorkloadViewModel;
	constructor(props: IProps) {
		super(props);
		if (!props.sendDate) {
			props.onSendDateUpdated(new Date());
		}

		this.mWorkloadViewModel = new AdminEmailWorkloadViewModel(props.userSession).impersonate(
			props.impersonationContext
		);
		this.state = {
			month: moment(props.sendDate).startOf('month').startOf('day').toDate(),
		};
	}

	public componentDidMount() {
		const { month } = this.state;
		if (!this.state.accountEmailWorkloadForSelectedMonth) {
			this.getAccountEmailWorkload(month);
		}
	}

	public render() {
		const { className, onRenderMessage, sendDate, styles, submitButtonText } = this.props;
		return (
			<div
				className={`${css(styleSheet.container, ...(styles || []))} bulk-email-approval-configure-schedule ${
					className || ''
				}`}
			>
				<MultiContainerHeader fullscreenHeader='Schedule Email for Clients: Schedule Send' />
				<ConfigureScheduledSend
					accountEmailWorkloadForSelectedMonth={this.state.accountEmailWorkloadForSelectedMonth}
					hideIcon={true}
					numberOfEmailsToSend={0}
					onDone={this.onDone}
					onMonthChanged={this.onMonthChanged}
					onRenderMessage={onRenderMessage}
					onSelectedDateChanged={this.onSelectedSendDateChanged}
					selectedDate={sendDate}
					submitButtonText={submitButtonText}
				/>
			</div>
		);
	}

	private onSelectedSendDateChanged = (selectedSendDate: Date) => {
		const { onSendDateUpdated } = this.props;
		if (onSendDateUpdated) {
			onSendDateUpdated(selectedSendDate);
		}
	};

	private onDone = (): Promise<any> => {
		const { onSelected } = this.props;
		if (onSelected) {
			onSelected();
		}

		return null;
	};

	private onMonthChanged = (month: Date) => {
		this.getAccountEmailWorkload(month);
	};

	private getAccountEmailWorkload = async (month: Date) => {
		const { logEvent, logApiError, impersonationContext } = this.props;
		if (impersonationContext?.isValid && !impersonationContext?.user?.id) {
			try {
				logEvent('GetAccountEmailWorkload');
				const startMoment = moment(month).startOf('month').subtract(2, 'weeks');
				const endMoment = moment(month).endOf('month').add(2, 'weeks');
				const opResult = await this.mWorkloadViewModel.getAccountWorkloadForDateRange(
					startMoment.toDate(),
					endMoment.toDate(),
					moment.tz.guess()
				);
				this.setState({
					accountEmailWorkloadForSelectedMonth: opResult.value,
				});
			} catch (error) {
				logApiError('GetAccountEmailWorkload-Error', error);
				if (this.state.accountEmailWorkloadForSelectedMonth) {
					this.setState({
						accountEmailWorkloadForSelectedMonth: null,
					});
				}
			}
		}
	};
}

const BulkEmailApprovalConfigureScheduleAsObserver = observer(_BulkEmailApprovalConfigureSchedule);
const BulkEmailApprovalConfigureScheduleWithContext = inject(
	UserSessionViewModelKey,
	ImpersonationContextKey
)(BulkEmailApprovalConfigureScheduleAsObserver);
export const BulkEmailApprovalConfigureSchedule = withEventLogging(
	BulkEmailApprovalConfigureScheduleWithContext,
	'BulkEmailApprovalConfigureSchedule'
);
